import { useCallback } from 'react';
import { Category, Bookmark } from '../../types/bookmarks';
import { NoteCategory, Note, NoteTemplate } from '../../types/notes';
import { CACHE_UPDATED_EVENT } from './useCacheFirstQuery';

const CACHE_KEYS = {
  categories: 'bookmark_cached_categories',
  bookmarks: 'bookmark_cached_bookmarks',
  noteCategories: 'note_cached_categories',
  notes: 'note_cached_notes',
  noteTemplates: 'note_cached_templates',
  timestamp: 'bookmark_cache_timestamp',
} as const;

type EntityType =
  | 'bookmark'
  | 'category'
  | 'note'
  | 'noteCategory'
  | 'noteTemplate';

interface CacheStore {
  getCategories: () => Category[] | null;
  getBookmarks: () => Bookmark[] | null;
  getNoteCategories: () => NoteCategory[] | null;
  getNotes: () => Note[] | null;
  getNoteTemplates: () => NoteTemplate[] | null;
  getTimestamp: () => string | null;
  setCategories: (categories: Category[]) => boolean;
  setBookmarks: (bookmarks: Bookmark[]) => boolean;
  setNoteCategories: (categories: NoteCategory[]) => boolean;
  setNotes: (notes: Note[]) => boolean;
  setNoteTemplates: (templates: NoteTemplate[]) => boolean;
  invalidate: (
    entityType: EntityType,
    operation: 'create' | 'update' | 'delete',
    entity: Bookmark | Category | Note | NoteCategory | NoteTemplate,
  ) => void;
  clear: () => void;
}

type CategoryLike = { category_id: string; parent_id: string | null };

/**
 * Finds all descendant category_ids of a given category by walking the parent_id chain.
 * Works for both bookmark categories and note categories (same shape).
 */
function findDescendantIds(
  categoryId: string,
  categories: CategoryLike[],
): string[] {
  const descendants: string[] = [];
  const queue = [categoryId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    for (const cat of categories) {
      if (cat.parent_id === currentId) {
        descendants.push(cat.category_id);
        queue.push(cat.category_id);
      }
    }
  }

  return descendants;
}

/**
 * Safely writes a value to localStorage.
 * Returns true on success, false on failure (e.g. quota exceeded).
 */
function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Clears all cache keys from localStorage.
 */
function clearAllCacheKeys(): void {
  try {
    localStorage.removeItem(CACHE_KEYS.categories);
    localStorage.removeItem(CACHE_KEYS.bookmarks);
    localStorage.removeItem(CACHE_KEYS.noteCategories);
    localStorage.removeItem(CACHE_KEYS.notes);
    localStorage.removeItem(CACHE_KEYS.noteTemplates);
    localStorage.removeItem(CACHE_KEYS.timestamp);
  } catch {
    // Nothing we can do if removeItem fails
  }
}

/**
 * Generic safe JSON read from localStorage. Returns null on miss or parse error.
 */
function safeGetArray<T>(key: string): T[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw) as T[];
  } catch {
    return null;
  }
}

/**
 * Writes an array under key, stamps the shared timestamp, and notifies listeners.
 */
function writeCacheArray<T>(key: string, value: T[]): boolean {
  const success = safeSetItem(key, JSON.stringify(value));
  if (success) {
    safeSetItem(CACHE_KEYS.timestamp, new Date().toISOString());
    window.dispatchEvent(new Event(CACHE_UPDATED_EVENT));
  }
  return success;
}

const useCacheStore = (): CacheStore => {
  const getCategories = useCallback((): Category[] | null => {
    try {
      const raw = localStorage.getItem(CACHE_KEYS.categories);
      if (raw === null) return null;
      return JSON.parse(raw) as Category[];
    } catch {
      return null;
    }
  }, []);

  const getBookmarks = useCallback((): Bookmark[] | null => {
    try {
      const raw = localStorage.getItem(CACHE_KEYS.bookmarks);
      if (raw === null) return null;
      return JSON.parse(raw) as Bookmark[];
    } catch {
      return null;
    }
  }, []);

  const getNoteCategories = useCallback(
    (): NoteCategory[] | null =>
      safeGetArray<NoteCategory>(CACHE_KEYS.noteCategories),
    [],
  );

  const getNotes = useCallback(
    (): Note[] | null => safeGetArray<Note>(CACHE_KEYS.notes),
    [],
  );

  const getNoteTemplates = useCallback(
    (): NoteTemplate[] | null =>
      safeGetArray<NoteTemplate>(CACHE_KEYS.noteTemplates),
    [],
  );

  const getTimestamp = useCallback((): string | null => {
    try {
      return localStorage.getItem(CACHE_KEYS.timestamp);
    } catch {
      return null;
    }
  }, []);

  const setCategories = useCallback((categories: Category[]): boolean => {
    const success = safeSetItem(
      CACHE_KEYS.categories,
      JSON.stringify(categories),
    );
    if (success) {
      safeSetItem(CACHE_KEYS.timestamp, new Date().toISOString());
      window.dispatchEvent(new Event(CACHE_UPDATED_EVENT));
    }
    return success;
  }, []);

  const setBookmarks = useCallback((bookmarks: Bookmark[]): boolean => {
    const success = safeSetItem(
      CACHE_KEYS.bookmarks,
      JSON.stringify(bookmarks),
    );
    if (success) {
      safeSetItem(CACHE_KEYS.timestamp, new Date().toISOString());
      window.dispatchEvent(new Event(CACHE_UPDATED_EVENT));
    }
    return success;
  }, []);

  const setNoteCategories = useCallback(
    (categories: NoteCategory[]): boolean =>
      writeCacheArray(CACHE_KEYS.noteCategories, categories),
    [],
  );

  const setNotes = useCallback(
    (notes: Note[]): boolean => writeCacheArray(CACHE_KEYS.notes, notes),
    [],
  );

  const setNoteTemplates = useCallback(
    (templates: NoteTemplate[]): boolean =>
      writeCacheArray(CACHE_KEYS.noteTemplates, templates),
    [],
  );

  const invalidate = useCallback(
    (
      entityType: EntityType,
      operation: 'create' | 'update' | 'delete',
      entity: Bookmark | Category | Note | NoteCategory | NoteTemplate,
    ): void => {
      switch (entityType) {
        case 'bookmark':
          invalidateBookmark(operation, entity as Bookmark);
          break;
        case 'category':
          invalidateCategory(operation, entity as Category);
          break;
        case 'note':
          invalidateNote(operation, entity as Note);
          break;
        case 'noteCategory':
          invalidateNoteCategory(operation, entity as NoteCategory);
          break;
        case 'noteTemplate':
          invalidateNoteTemplate(operation, entity as NoteTemplate);
          break;
      }
      window.dispatchEvent(new Event(CACHE_UPDATED_EVENT));
    },
    [],
  );

  const clear = useCallback((): void => {
    clearAllCacheKeys();
  }, []);

  return {
    getCategories,
    getBookmarks,
    getNoteCategories,
    getNotes,
    getNoteTemplates,
    getTimestamp,
    setCategories,
    setBookmarks,
    setNoteCategories,
    setNotes,
    setNoteTemplates,
    invalidate,
    clear,
  };
};

function invalidateBookmark(
  operation: 'create' | 'update' | 'delete',
  bookmark: Bookmark,
): void {
  try {
    const raw = localStorage.getItem(CACHE_KEYS.bookmarks);
    if (raw === null) return;

    let bookmarks: Bookmark[] = JSON.parse(raw);

    switch (operation) {
      case 'create':
        bookmarks.push(bookmark);
        break;
      case 'update':
        bookmarks = bookmarks.map((b) =>
          b.bookmark_id === bookmark.bookmark_id ? bookmark : b,
        );
        break;
      case 'delete':
        bookmarks = bookmarks.filter(
          (b) => b.bookmark_id !== bookmark.bookmark_id,
        );
        break;
    }

    const success = safeSetItem(
      CACHE_KEYS.bookmarks,
      JSON.stringify(bookmarks),
    );
    if (!success) {
      clearAllCacheKeys();
      return;
    }

    const tsSuccess = safeSetItem(
      CACHE_KEYS.timestamp,
      new Date().toISOString(),
    );
    if (!tsSuccess) {
      clearAllCacheKeys();
    }
  } catch {
    clearAllCacheKeys();
  }
}

function invalidateCategory(
  operation: 'create' | 'update' | 'delete',
  category: Category,
): void {
  try {
    const raw = localStorage.getItem(CACHE_KEYS.categories);
    if (raw === null) return;

    let categories: Category[] = JSON.parse(raw);

    switch (operation) {
      case 'create':
        categories.push(category);
        break;
      case 'update':
        categories = categories.map((c) =>
          c.category_id === category.category_id ? category : c,
        );
        break;
      case 'delete': {
        const descendantIds = findDescendantIds(
          category.category_id,
          categories,
        );
        const idsToRemove = new Set([category.category_id, ...descendantIds]);
        categories = categories.filter((c) => !idsToRemove.has(c.category_id));
        break;
      }
    }

    const success = safeSetItem(
      CACHE_KEYS.categories,
      JSON.stringify(categories),
    );
    if (!success) {
      clearAllCacheKeys();
      return;
    }

    const tsSuccess = safeSetItem(
      CACHE_KEYS.timestamp,
      new Date().toISOString(),
    );
    if (!tsSuccess) {
      clearAllCacheKeys();
    }
  } catch {
    clearAllCacheKeys();
  }
}

function invalidateNote(
  operation: 'create' | 'update' | 'delete',
  note: Note,
): void {
  try {
    const raw = localStorage.getItem(CACHE_KEYS.notes);
    if (raw === null) return;

    let notes: Note[] = JSON.parse(raw);

    switch (operation) {
      case 'create':
        notes.push(note);
        break;
      case 'update':
        notes = notes.map((n) => (n.note_id === note.note_id ? note : n));
        break;
      case 'delete':
        // Removal from the active cache (covers both soft-delete leaving the
        // active set and hard-delete).
        notes = notes.filter((n) => n.note_id !== note.note_id);
        break;
    }

    if (!writeCacheArray(CACHE_KEYS.notes, notes)) {
      clearAllCacheKeys();
    }
  } catch {
    clearAllCacheKeys();
  }
}

function invalidateNoteCategory(
  operation: 'create' | 'update' | 'delete',
  category: NoteCategory,
): void {
  try {
    const raw = localStorage.getItem(CACHE_KEYS.noteCategories);
    if (raw === null) return;

    let categories: NoteCategory[] = JSON.parse(raw);

    switch (operation) {
      case 'create':
        categories.push(category);
        break;
      case 'update':
        categories = categories.map((c) =>
          c.category_id === category.category_id ? category : c,
        );
        break;
      case 'delete': {
        const descendantIds = findDescendantIds(
          category.category_id,
          categories,
        );
        const idsToRemove = new Set([category.category_id, ...descendantIds]);
        categories = categories.filter((c) => !idsToRemove.has(c.category_id));
        break;
      }
    }

    if (!writeCacheArray(CACHE_KEYS.noteCategories, categories)) {
      clearAllCacheKeys();
    }
  } catch {
    clearAllCacheKeys();
  }
}

function invalidateNoteTemplate(
  operation: 'create' | 'update' | 'delete',
  template: NoteTemplate,
): void {
  try {
    const raw = localStorage.getItem(CACHE_KEYS.noteTemplates);
    if (raw === null) return;

    let templates: NoteTemplate[] = JSON.parse(raw);

    switch (operation) {
      case 'create':
        templates.push(template);
        break;
      case 'update':
        templates = templates.map((t) =>
          t.template_id === template.template_id ? template : t,
        );
        break;
      case 'delete':
        templates = templates.filter(
          (t) => t.template_id !== template.template_id,
        );
        break;
    }

    if (!writeCacheArray(CACHE_KEYS.noteTemplates, templates)) {
      clearAllCacheKeys();
    }
  } catch {
    clearAllCacheKeys();
  }
}

export { useCacheStore, CACHE_KEYS };
